import { CalendarBlank, CaretLeft, CaretRight } from '@phosphor-icons/react';

import { HelpTooltip } from './HelpTooltip';
import { cn } from '../../utils/cn';

/**
 * `.pen` → `ds/nav/cycle-nav` (`ohNys`).
 *
 * Dois tons, ambos definidos no `.pen`:
 * - `surface` — o master tal como está no library: setas 34×34 `$surface-card`
 *   com contorno `$border`, ícones 16 `$fg-muted`, `justifyContent
 *   space_between`, chip `ds/badge/status--sage` (`$sage-tint` / `$sage-on-tint`,
 *   `$type-chip`).
 * - `on-hero` — o override que os heroes de ciclo aplicam à instância
 *   (`ds/card/cycle-spend-hero` › `cycle-nav`): setas 26×26 `$on-hero-track`
 *   sem contorno, ícones 14 `$on-hero`, `justifyContent start`, `gap $space-2`,
 *   chip `$on-hero-track` / `$on-hero` com o rótulo em `$type-section`.
 *
 * 34/26px não têm token `$space-*` — são as medidas do controlo no `.pen`,
 * por isso vivem em constantes de módulo em vez de literais inline no JSX.
 */
const TONES = {
  surface: {
    row: 'justify-between gap-space-2',
    control:
      'flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-ds-full border border-border bg-card text-muted-foreground transition-colors hover:bg-muted disabled:opacity-40 disabled:hover:bg-card',
    spacer: 'h-[34px] w-[34px] shrink-0',
    icon: 'h-4 w-4',
    chip: 'bg-sage-tint text-sage-on-tint gap-space-1 px-space-2-5 py-space-1',
    chipIcon: 'h-3 w-3',
    chipText: 'type-chip',
  },
  'on-hero': {
    row: 'justify-start gap-space-2',
    control:
      'flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-ds-full bg-sage-50/20 text-sage-50 transition-opacity hover:opacity-80 disabled:opacity-40',
    spacer: 'h-[26px] w-[26px] shrink-0',
    icon: 'h-3.5 w-3.5',
    chip: 'bg-sage-50/20 text-sage-50 gap-space-2 px-space-3 py-space-1',
    chipIcon: 'h-3.5 w-3.5',
    chipText: 'type-section normal-case',
  },
};

/**
 * @param {object} props
 * @param {object[]} [props.cycles]
 * @param {number} [props.cycleIndex]
 * @param {(index: number) => void} [props.onCycleIndexChange]
 * @param {string} props.label — texto do chip (ex.: «Ciclo · 1–31 ago»)
 * @param {string} props.prevAriaLabel
 * @param {string} props.nextAriaLabel
 * @param {string} [props.tooltip] — quando definido, o chip ganha um `HelpTooltip`
 * @param {'surface' | 'on-hero'} [props.tone]
 * @param {string} [props.className]
 */
export function CycleChipNav({
  cycles = [],
  cycleIndex = 0,
  onCycleIndexChange,
  label,
  prevAriaLabel,
  nextAriaLabel,
  tooltip,
  tone = 'surface',
  className,
}) {
  const t = TONES[tone] || TONES.surface;
  const hasNav = cycles.length > 1;
  const canGoPrev = cycleIndex < cycles.length - 1;
  const canGoNext = cycleIndex > 0;

  return (
    <div className={cn('flex w-full min-w-0 items-center', t.row, className)}>
      {hasNav ? (
        <button
          type="button"
          className={t.control}
          disabled={!canGoPrev}
          aria-label={prevAriaLabel}
          onClick={() => onCycleIndexChange(Math.min(cycles.length - 1, cycleIndex + 1))}
        >
          <CaretLeft className={t.icon} aria-hidden />
        </button>
      ) : (
        <span className={t.spacer} aria-hidden />
      )}

      <span
        className={cn('inline-flex min-w-0 items-center rounded-ds-full', t.chip)}
      >
        <CalendarBlank className={cn('shrink-0', t.chipIcon)} aria-hidden />
        <span className={cn('truncate', t.chipText)}>{label}</span>
        {tooltip ? <HelpTooltip label={tooltip} content={tooltip} /> : null}
      </span>

      {hasNav ? (
        <button
          type="button"
          className={t.control}
          disabled={!canGoNext}
          aria-label={nextAriaLabel}
          onClick={() => onCycleIndexChange(Math.max(0, cycleIndex - 1))}
        >
          <CaretRight className={t.icon} aria-hidden />
        </button>
      ) : (
        <span className={t.spacer} aria-hidden />
      )}
    </div>
  );
}

export default CycleChipNav;
